<template>
  <div class="space-y-8">
    <!-- Progress Steps -->
    <div
      class="flex items-center justify-center mb-8 border-2 py-12 border-black rounded-2xl p-6 max-w-3xl mx-auto bg-white shadow-[6px_6px_0px_#000]"
    >
      <div class="flex items-center space-x-4">
        <!-- Step 1 -->
        <div class="flex items-center">
          <div class="step-indicator completed">
            <span class="text-white font-semibold">1</span>
          </div>
          <span class="ml-2 text-sm font-medium text-gray-700">Select Date & Time</span>
        </div>

        <!-- Connector -->
        <div class="w-16 h-1 bg-blue-500 rounded"></div>

        <!-- Step 2 -->
        <div class="flex items-center">
          <div class="step-indicator active">
            <span class="text-white font-semibold">2</span>
          </div>
          <span class="ml-2 text-sm font-medium text-gray-900">Session Details</span>
        </div>
      </div>
    </div>

    <!-- Configure Your Session Card -->
    <div
      class="border-2 pt-12 pb-6 border-black rounded-2xl pt-6 pb-3 px-12 max-w-3xl mx-auto bg-white shadow-[6px_6px_0px_#000]"
    >
      <div class="text-center mb-8">
        <h2 class="text-2xl font-bold text-gray-900">Configure Your Session</h2>
        <p class="text-gray-600 mt-2">
          Tell us what you'd like to learn and how you'd prefer to meet
        </p>
      </div>

      <form @submit.prevent="handleSubmit" class="space-y-6">
        <!-- Subject and Mode Selection -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div class="lg:pr-12">
            <label class="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1.5">
              <AcademicCapIcon class="w-4 h-4" />
              Subject <span class="text-red-500">*</span>
            </label>
            <select
              v-model="form.subject"
              :class="[
                'custom-select w-full px-4 py-2 border-2 rounded-lg focus:ring-2 placeholder:text-gray-400 cursor-pointer',
                !form.subject && formTouched
                  ? 'border-red-500 focus:border-red-500 focus:ring-red-200'
                  : 'border-black shadow-[2px_2px_0_0_rgba(0,0,0,1)] focus:border-blue-500 focus:ring-blue-500'
              ]"
            >
              <option value="">Select a subject</option>
              <option v-for="subject in availableSubjects" :key="subject" :value="subject">
                {{ subject }}
              </option>
            </select>
          </div>

          <div class="lg:pr-12">
            <label class="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1.5">
              <VideoCameraIcon class="w-4 h-4" />
              Session Mode <span class="text-red-500">*</span>
            </label>
            <select
              v-model="form.mode"
              :class="[
                'custom-select w-full px-4 py-2 border-2 rounded-lg focus:ring-2 placeholder:text-gray-400 cursor-pointer',
                !form.mode && formTouched
                  ? 'border-red-500 focus:border-red-500 focus:ring-red-200'
                  : 'border-black shadow-[2px_2px_0_0_rgba(0,0,0,1)] focus:border-blue-500 focus:ring-blue-500'
              ]"
            >
              <option value="">Select a mode</option>
              <option v-for="mode in availableModes" :key="mode.value" :value="mode.value">
                {{ mode.label }}
              </option>
            </select>
          </div>
        </div>

        <!-- Location Field (Face-to-Face only) -->
        <div v-if="form.mode === 'face-to-face'" class="animate-in slide-in-from-top duration-300">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1.5">
              <MapPinIcon class="w-4 h-4" />
              Preferred Location <span class="text-red-500">*</span>
            </label>

            <!-- Tutor's City (if available) - Subtle inline version -->
            <div
              v-if="tutor?.city"
              class="mb-2.5 flex items-center gap-2 text-sm p-3 border border-blue-200 bg-blue-50 rounded-lg"
            >
              <InformationCircleIcon class="w-4 h-4 flex-shrink-0 text-blue-500" />
              <span class="text-blue-600"
                >This tutor's area is at <span class="font-medium">{{ tutor.city }}</span
                >.</span
              >
            </div>

            <!-- Message when tutor hasn't set city -->
            <div
              v-else
              class="mb-2.5 flex items-center gap-2 text-sm p-3 border border-gray-200 bg-gray-50 rounded-lg"
            >
              <InformationCircleIcon class="w-4 h-4 flex-shrink-0 text-gray-400" />
              <span class="text-gray-500">This tutor has not set their service area yet.</span>
            </div>

            <input
              v-model="form.location"
              type="text"
              minlength="4"
              maxlength="100"
              @blur="locationTouched = true"
              :class="[
                'w-full px-4 py-2.5 border-2 rounded-lg focus:ring-2 placeholder:text-gray-400',
                locationError && locationTouched
                  ? 'border-red-500 focus:border-red-500 focus:ring-red-200' 
                  : 'border-black shadow-[2px_2px_0_0_rgba(0,0,0,1)] focus:border-blue-500 focus:ring-blue-500'
              ]"
              placeholder="e.g., Library, Coffee Shop"
            />
            <p class="text-xs mt-2" :class="locationError && locationTouched ? 'text-red-600' : 'text-gray-400'">
              <span v-if="locationError && locationTouched" class="font-medium">{{ locationError }}</span>
              <span v-else class="font-medium text-blue-600">Must be a public place for safety.</span>
            </p>
          </div>
        </div>

        <!-- Age Verification Warning (Face-to-Face only) -->
        <div v-if="showAgeWarning" class="animate-in slide-in-from-top duration-300">
          <div class="bg-amber-50 border-2 border-amber-200 rounded-lg p-4">
            <div class="flex items-start">
              <ExclamationTriangleIcon class="w-5 h-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0" />
              <div class="flex-1">
                <h3 class="text-sm font-medium text-amber-800 mb-1">Age Restriction Notice</h3>
                <p class="text-sm text-amber-700">
                  {{ ageWarningMessage }}
                </p>
                <p class="text-xs text-amber-600 mt-2">
                  You can still schedule online appointments which are available to all students.
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Learning Goals -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1.5">
            <LightBulbIcon class="w-4 h-4" />
            What would you like to learn from your tutor? <span class="text-red-500">*</span>
          </label>
          <textarea
            v-model="form.learningGoals"
            rows="6"
            maxlength="300"
            class="w-full px-4 py-3 border-2 border-black shadow-[2px_2px_0_0_rgba(0,0,0,1)] rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder:text-gray-400"
            :class="{ 'border-red-500': learningGoalsValidationError }"
            placeholder="Describe what you want to learn, specific topics you need help with, or goals you want to achieve in this session. For example: 'I need help understanding quadratic equations and solving word problems for my upcoming exam.'"
            required
          ></textarea>
          <div class="flex justify-between items-center mt-2">
            <div class="flex-1">
              <p v-if="!learningGoalsValidationError" class="text-xs text-gray-500">
                Be as specific as possible to help your tutor prepare for the session.
              </p>
              <p v-else class="text-xs text-red-600 font-medium">
                {{ learningGoalsValidationError }}
              </p>
            </div>
            <span
              class="text-xs font-medium ml-2"
              :class="
                learningGoalsCharCount >= 300
                  ? 'text-red-600'
                  : learningGoalsCharCount < 10
                  ? 'text-orange-600'
                  : 'text-gray-500'
              "
            >
              {{ learningGoalsCharCount }}/300
            </span>
          </div>
        </div>

        <!-- Additional Materials Upload -->
        <div
          class="border-2 border-dashed border-purple-200 rounded-lg p-6 bg-gradient-to-br from-purple-50/50 via-white to-transparent hover:from-purple-100/50 transition-all"
        >
          <label class="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
            <PaperClipIcon class="w-5 h-5 text-purple-600" />
            Additional Materials (Optional)
          </label>
          <p class="text-xs text-gray-600 mb-4">
            Upload reference materials like PowerPoint slides, PDFs, images, or documents to help
            your tutor understand what you need help with.
            <span class="font-medium text-gray-700">Max 5 files, 10MB each.</span>
          </p>

          <!-- File Upload Input -->
          <div class="relative">
            <input
              ref="fileInput"
              type="file"
              @change="handleFileSelect"
              multiple
              accept=".pdf,.doc,.docx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif,.webp"
              class="hidden"
            />
            <button
              type="button"
              @click="$refs.fileInput?.click()"
              :disabled="uploadedFiles.length >= 5"
              class="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white border-2 border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              <svg
                class="w-5 h-5 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
              <span class="text-sm font-medium text-gray-700">
                {{ uploadedFiles.length >= 5 ? "Maximum files reached" : "Choose files" }}
              </span>
            </button>
          </div>

          <!-- Allowed file types hint -->
          <p class="text-xs text-gray-500 mt-2 text-center">
            Supported: PDF, Word, PowerPoint, TXT, Images (JPG, PNG, GIF, WEBP)
          </p>

          <!-- Uploaded Files List -->
          <div v-if="uploadedFiles.length > 0" class="mt-4 space-y-2">
            <div
              v-for="(file, index) in uploadedFiles"
              :key="index"
              class="flex items-center justify-between bg-white border-2 border-gray-200 rounded-lg p-3 group hover:border-blue-300 transition-colors"
            >
              <div class="flex items-center gap-3 flex-1 min-w-0">
                <!-- Image Preview or File Icon -->
                <div class="flex-shrink-0">
                  <img
                    v-if="file.type.startsWith('image/')"
                    :src="file.url"
                    :alt="file.name"
                    class="w-12 h-12 object-cover rounded border border-gray-200"
                  />
                  <span v-else class="text-2xl">{{ getFileIcon(file.type) }}</span>
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-medium text-gray-900 truncate">{{ file.name }}</p>
                  <p class="text-xs text-gray-500">{{ formatFileSize(file.size) }}</p>
                </div>
              </div>
              <button
                type="button"
                @click="removeFile(index)"
                class="flex-shrink-0 ml-2 p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Remove file"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          <!-- Upload Progress -->
          <div v-if="isUploading" class="mt-4">
            <div class="flex items-center justify-center gap-2 text-blue-600">
              <div class="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
              <span class="text-sm font-medium">Uploading files...</span>
            </div>
          </div>

          <!-- Upload Error -->
          <div v-if="uploadError" class="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p class="text-sm text-red-600">{{ uploadError }}</p>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="flex justify-between pt-6 pb-2">
          <button
            type="button"
            @click="$emit('previous-step')"
            class="flex items-center gap-2 px-4 py-1 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
          >
            <ChevronLeftIcon class="w-4 h-4" />
            Previous
          </button>

          <button
            type="submit"
            :class="[
              'next-button flex items-center gap-2 px-3 py-1 rounded-lg border font-medium transition-colors',
              isBooking
                ? 'bg-gray-400 text-white border-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white border-blue-800 shadow-[4px_4px_0px_#193CB8] cursor-pointer'
            ]"
            :disabled="isBooking"
          >
            <div
              v-if="isBooking"
              class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"
            ></div>
            <span v-if="isBooking">Scheduling Appointment...</span>
            <span v-else class="flex items-center gap-2">
              Schedule Appointment
              <CalendarIcon class="w-4 h-4" />
            </span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import {
  CalendarIcon,
  ChevronLeftIcon,
  ExclamationTriangleIcon,
  PaperClipIcon,
  InformationCircleIcon,
  AcademicCapIcon,
  VideoCameraIcon,
  MapPinIcon,
  LightBulbIcon,
} from "@heroicons/vue/24/outline";
import { verifyAgeForSession, getAgeRestrictionMessage } from "@/utils/ageVerification";
import { doc, getDoc } from "firebase/firestore";
import { db } from "@/firebase";
import { auth } from "@/firebase";
import { logDebug, logError, logWarn } from "@/utils/secureLogger";
import {
  validateFile,
  uploadSessionMaterial,
  formatFileSize,
  getFileIcon,
  MAX_FILES,
} from "@/utils/fileUpload";
import { comprehensiveSecurityCheck } from "@/utils/contentSafetyFilter";
import Swal from "sweetalert2";

const props = defineProps({
  tutor: {
    type: Object,
    default: null,
  },
  currentStep: {
    type: Number,
    default: 1,
  },
  selectedDateTime: {
    type: Object,
    default: null,
  },
  isBooking: {
    type: Boolean,
    default: false,
  },
  initialFormData: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(["next-step", "previous-step", "session-configured"]);

const form = ref({
  subject: "", // Start empty to show placeholder
  mode: "",
  learningGoals: "",
  location: "",
});

// ✅ NEW: Restore form data if user is returning from PreTest
if (props.initialFormData) {
  form.value = {
    subject: props.initialFormData.subject || "",
    mode: props.initialFormData.mode || "",
    learningGoals: props.initialFormData.learningGoals || "",
    location: props.initialFormData.location || "",
  };
  logDebug("✅ Form data restored from previous submission", form.value);
}

// File upload state
const fileInput = ref(null);
const uploadedFiles = ref([]);
const isUploading = ref(false);
const uploadError = ref("");

// ✅ NEW: Restore uploaded files if user is returning from PreTest
if (props.initialFormData?.materials && props.initialFormData.materials.length > 0) {
  uploadedFiles.value = [...props.initialFormData.materials];
  logDebug("✅ Uploaded files restored", { count: uploadedFiles.value.length });
}

// Learning goals validation state
const learningGoalsValidationError = ref("");

// Location validation state
const locationError = ref("");
const locationTouched = ref(false);
const formTouched = ref(false);

// Age verification state
const userProfile = ref(null);
const ageVerification = ref(null);
const isLoadingProfile = ref(false);

// Load user profile on component mount
onMounted(async () => {
  await loadUserProfile();
});

// Watch for mode changes to clear location when switching to online
watch(
  () => form.value.mode,
  (newMode) => {
    if (newMode === "online") {
      form.value.location = "";
      locationError.value = "";
      locationTouched.value = false;
    }
    // Check age verification when mode changes
    checkAgeVerification();
  }
);

// Watch for location changes (only validate if touched)
watch(
  () => form.value.location,
  () => {
    if (form.value.mode === "face-to-face" && locationTouched.value) {
      validateLocation();
    }
  }
);

// Watch for user profile changes
watch(
  () => userProfile.value,
  () => {
    checkAgeVerification();
  }
);

// ✅ NEW: Watch learning goals for real-time validation
watch(
  () => form.value.learningGoals,
  (newValue) => {
    validateLearningGoals(newValue);
  }
);

// ✅ NEW: Computed property for character count (excluding spaces)
const learningGoalsCharCount = computed(() => {
  return form.value.learningGoals.replace(/\s/g, "").length;
});

// Load user profile from Firestore
const loadUserProfile = async () => {
  try {
    isLoadingProfile.value = true;
    const currentUser = auth.currentUser;

    if (!currentUser) {
      logError("No authenticated user found");
      return;
    }

    const userDoc = await getDoc(doc(db, "users", currentUser.uid));

    if (userDoc.exists()) {
      userProfile.value = userDoc.data();
      logDebug("User profile loaded", { hasDateOfBirth: !!userProfile.value.dateOfBirth });
    } else {
      logError("User profile not found");
    }
  } catch (error) {
    logError("Error loading user profile", error);
  } finally {
    isLoadingProfile.value = false;
  }
};

// Check age verification for current mode
const checkAgeVerification = () => {
  if (!userProfile.value?.dateOfBirth || !form.value.mode) {
    ageVerification.value = null;
    return;
  }

  ageVerification.value = verifyAgeForSession(userProfile.value.dateOfBirth, form.value.mode);
  logDebug("Age verification checked", {
    mode: form.value.mode,
    isValid: ageVerification.value.isValid,
    age: ageVerification.value.age,
  });
};

// ✅ NEW: Validate learning goals input
const validateLearningGoals = (text) => {
  learningGoalsValidationError.value = "";

  // Skip validation if empty (will be caught by required attribute)
  if (!text || text.trim().length === 0) {
    return;
  }

  // Check minimum character count (excluding spaces)
  const charCountWithoutSpaces = text.replace(/\s/g, "").length;
  if (charCountWithoutSpaces < 10) {
    learningGoalsValidationError.value = "Minimum 10 characters required (excluding spaces)";
    return;
  }

  // Check maximum character count (excluding spaces)
  if (charCountWithoutSpaces > 300) {
    learningGoalsValidationError.value = "Maximum 300 characters allowed (excluding spaces)";
    return;
  }

  // ✅ SECURITY: Comprehensive security check FIRST (profanity, SQL injection, XSS, DDoS)
  // This matches AINoteProcessor.vue's comprehensive validation approach
  const securityCheck = comprehensiveSecurityCheck(text);

  if (!securityCheck.isSafe || securityCheck.blocked) {
    learningGoalsValidationError.value =
      securityCheck.message || "This content cannot be processed";

    logWarn("⚠️ SECURITY THREAT DETECTED IN LEARNING GOALS", {
      textLength: text.length,
      severity: securityCheck.severity,
      reasons: securityCheck.reasons,
      detectedTerms: securityCheck.detectedTerms?.slice(0, 3),
    });

    const isCritical = securityCheck.severity === "critical";
    const isHighSeverity = securityCheck.severity === "high" || isCritical;

    // Immediately clear the unsafe content and show warning (matches AINoteProcessor.vue)
    Swal.fire({
      icon: isHighSeverity ? "error" : "warning",
      title: isCritical
        ? "🚨 Security Threat Blocked"
        : isHighSeverity
        ? "⚠️ Suspicious Input Detected"
        : "Inappropriate Content Detected",
      html: `
        <div class="text-left space-y-3">
          ${
            isCritical
              ? '<div class="bg-red-50 border-2 border-red-300 rounded-lg p-3 mb-3">' +
                '<p class="text-red-800 font-bold mb-2">⚠️ CRITICAL SECURITY ALERT</p>' +
                '<p class="text-red-700 text-sm">' +
                securityCheck.message +
                "</p>" +
                "</div>"
              : '<p class="mb-3 text-gray-700">' +
                (securityCheck.message ||
                  securityCheck.reason ||
                  "The text you entered contains content that cannot be processed.") +
                "</p>"
          }
          
          ${
            securityCheck.reasons && securityCheck.reasons.length > 0
              ? '<div class="bg-gray-50 p-3 rounded-lg border border-gray-200 mb-3">' +
                '<p class="font-semibold text-sm mb-1">Security Issues Detected:</p>' +
                '<ul class="text-sm text-gray-700 list-disc pl-5 space-y-1">' +
                securityCheck.reasons.map((reason) => `<li>${reason}</li>`).join("") +
                "</ul>" +
                "</div>"
              : ""
          }

          <div class="bg-blue-50 p-3 rounded-lg border border-blue-200">
            <p class="font-semibold text-sm mb-2 text-blue-800">✅ ElevatED is a safe learning platform:</p>
            <ul class="text-sm text-blue-700 list-disc pl-5 space-y-1">
              <li>Educational and age-appropriate content only</li>
              <li>No profanity, explicit language, or violence</li>
              <li>Protection against malicious code injection</li>
              <li>Suitable for high school students</li>
            </ul>
          </div>

          <p class="text-sm text-gray-600 mt-3"><strong>The unsafe content has been automatically removed.</strong></p>
        </div>
      `,
      confirmButtonColor: isHighSeverity ? "#dc2626" : "#ef4444",
      confirmButtonText: "I Understand",
      allowOutsideClick: false,
      allowEscapeKey: false,
    });

    // Clear the unsafe content (matches AINoteProcessor.vue behavior)
    form.value.learningGoals = "";
    return;
  }

  // ✅ GIBBERISH CHECK: After security check, validate meaningful text
  // Check for excessive special characters (but allow normal punctuation)
  const specialCharRatio = (text.match(/[^a-zA-Z0-9\s]/g) || []).length / text.length;
  const mostlySymbols = /^[\s\W]+$/.test(text); // Only symbols/special chars

  if (mostlySymbols || specialCharRatio > 0.7) {
    learningGoalsValidationError.value =
      "Please enter meaningful text, not just symbols or special characters";

    logWarn("Learning goals validation: Too many special characters", {
      text: text.substring(0, 50),
      specialCharRatio,
    });

    Swal.fire({
      icon: "warning",
      title: "Invalid Input",
      html: `
        <div class="text-left">
          <p class="mb-3">Your learning goals contain too many special characters or symbols.</p>
          <div class="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4">
            <p class="text-sm text-gray-700">Please describe what you want to learn using regular text.</p>
            <p class="text-sm text-gray-700 mt-2"><strong>Example:</strong> "I need help understanding quadratic equations"</p>
          </div>
        </div>
      `,
      confirmButtonText: "OK",
      confirmButtonColor: "#F59E0B",
    });

    // Clear the gibberish input
    form.value.learningGoals = "";
    return;
  }

  // ✅ All checks passed
  learningGoalsValidationError.value = "";
};

// Age verification computed properties
const showAgeWarning = computed(() => {
  return (
    form.value.mode === "face-to-face" && ageVerification.value && !ageVerification.value.isValid
  );
});

const ageWarningMessage = computed(() => {
  if (!ageVerification.value) return "";
  return getAgeRestrictionMessage(ageVerification.value.age, form.value.mode);
});

// Validate location input
const validateLocation = () => {
  locationError.value = "";
  
  if (form.value.mode !== "face-to-face") {
    return;
  }

  const trimmedLocation = form.value.location.trim();
  
  if (!trimmedLocation) {
    locationError.value = "Location is required for face-to-face sessions";
    return;
  }

  if (trimmedLocation.length < 4) {
    locationError.value = "Location must be at least 4 characters";
    return;
  }

  if (trimmedLocation.length > 100) {
    locationError.value = "Location must not exceed 100 characters";
    return;
  }
};

const canBookSession = computed(() => {
  const basicValidation =
    form.value.subject &&
    form.value.mode &&
    form.value.learningGoals.trim() &&
    learningGoalsCharCount.value >= 10 && // ✅ NEW: Min 10 chars (excluding spaces)
    learningGoalsCharCount.value <= 300 && // ✅ NEW: Max 300 chars (excluding spaces)
    !learningGoalsValidationError.value && // ✅ NEW: No validation errors
    (form.value.mode !== "face-to-face" || (form.value.location.trim() && form.value.location.trim().length >= 4 && form.value.location.trim().length <= 100));

  // ✅ FIX: Disable button while files are uploading
  if (isUploading.value) {
    return false;
  }

  // For face-to-face sessions, also check age verification
  if (form.value.mode === "face-to-face") {
    return basicValidation && ageVerification.value && ageVerification.value.isValid && !locationError.value;
  }

  return basicValidation;
});

// Available subjects based on tutor's expertise
const availableSubjects = computed(() => {
  return (
    props.tutor?.subjects || [
      "General Mathematics",
      "Statistics and Probability",
      "Pre-Calculus",
      "Algebra",
      "Geometry",
      "Calculus",
    ]
  );
});

// Available modes based on selected time slot
const availableModes = computed(() => {
  if (!props.selectedDateTime?.dateTime?.timeSlot?.mode) {
    // Fallback to both modes if no specific mode is set
    return [
      { value: "online", label: "Online" },
      { value: "face-to-face", label: "Face-to-Face" },
    ];
  }

  const selectedMode = props.selectedDateTime.dateTime.timeSlot.mode.toLowerCase();

  // If the availability supports "both", show both options
  if (selectedMode === "both") {
    return [
      { value: "online", label: "Online" },
      { value: "face-to-face", label: "Face-to-Face" },
    ];
  }

  // If specific mode, only show that mode
  if (selectedMode === "online") {
    return [{ value: "online", label: "Online" }];
  }

  if (selectedMode === "face-to-face" || selectedMode === "in-person") {
    return [{ value: "face-to-face", label: "Face-to-Face" }];
  }

  // Default fallback
  return [
    { value: "online", label: "Online" },
    { value: "face-to-face", label: "Face-to-Face" },
  ];
});

// File upload handlers
const handleFileSelect = async (event) => {
  const files = Array.from(event.target.files || []);

  if (!files.length) return;

  const remainingSlots = MAX_FILES - uploadedFiles.value.length;

  // Check total file count - reject if would exceed limit
  if (files.length > remainingSlots) {
    await Swal.fire({
      icon: "warning",
      title: "Too Many Files",
      html: `You can only upload up to <strong>5 files total</strong>.<br><br>You currently have <strong>${uploadedFiles.value.length}</strong> file(s) uploaded, so you can add <strong>${remainingSlots}</strong> more.<br><br>Please select ${remainingSlots} or fewer files.`,
      confirmButtonText: "OK",
      confirmButtonColor: "#3b82f6",
    });
    // Clear the input
    if (fileInput.value) {
      fileInput.value.value = "";
    }
    return;
  }

  uploadError.value = "";
  isUploading.value = true;

  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error("You must be logged in to upload files");
    }

    // ✅ SECURITY: Validate all files first with comprehensive checks (async)
    for (const file of files) {
      const validation = await validateFile(file);
      if (!validation.valid) {
        uploadError.value = validation.error;
        isUploading.value = false;
        return;
      }
    }

    // Upload files to temporary storage (will be moved when session is created)
    const uploadPromises = files.map((file) => uploadSessionMaterial(file, currentUser.uid));

    const uploadedFileData = await Promise.all(uploadPromises);

    // ✅ FIX: Keep original File objects for PreTest (avoids CORS issues)
    // Attach original file to each uploaded file metadata
    const filesWithOriginals = uploadedFileData.map((uploadData, index) => ({
      ...uploadData,
      originalFile: files[index], // Keep original File object
    }));

    // Add to uploaded files list
    uploadedFiles.value.push(...filesWithOriginals);

    // Clear the input
    if (fileInput.value) {
      fileInput.value.value = "";
    }

    logDebug("Files uploaded successfully", { count: uploadedFileData.length });
  } catch (error) {
    logError("Error uploading files", error);
    uploadError.value = error.message || "Failed to upload files. Please try again.";
  } finally {
    isUploading.value = false;
  }
};

const removeFile = (index) => {
  uploadedFiles.value.splice(index, 1);
  uploadError.value = "";
};

const handleSubmit = () => {
  // Mark form as touched
  formTouched.value = true;
  
  // Validate all fields
  validateLearningGoals(form.value.learningGoals);
  if (form.value.mode === "face-to-face") {
    locationTouched.value = true; // Mark as touched when submitting
    validateLocation();
  }

  // Check for validation errors
  if (learningGoalsValidationError.value) {
    // Scroll to learning goals field
    const learningGoalsTextarea = document.querySelector('textarea[maxlength="300"]');
    if (learningGoalsTextarea) {
      learningGoalsTextarea.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => learningGoalsTextarea.focus(), 300);
    }
    return;
  }

  if (locationError.value) {
    // Scroll to location field
    const locationInput = document.querySelector('input[type="text"][placeholder*="Library"]');
    if (locationInput) {
      locationInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => locationInput.focus(), 300);
    }
    return;
  }

  // Check if required fields are filled
  if (!form.value.subject) {
    Swal.fire({
      icon: "error",
      title: "Missing Subject",
      text: "Please select a subject for the session.",
      confirmButtonColor: "#DC2626",
    });
    return;
  }

  if (!form.value.mode) {
    Swal.fire({
      icon: "error",
      title: "Missing Mode",
      text: "Please select a session mode (Online or Face-to-Face).",
      confirmButtonColor: "#DC2626",
    });
    return;
  }

  // Check age verification for face-to-face sessions
  if (
    form.value.mode === "face-to-face" &&
    ageVerification.value &&
    !ageVerification.value.isValid
  ) {
    // Don't proceed if age verification fails
    return;
  }

  // Validate required fields including location for face-to-face
  const isValid = canBookSession.value;

  if (isValid) {
    const sessionConfig = {
      subject: form.value.subject,
      mode: form.value.mode,
      learningGoals: form.value.learningGoals,
      tutorId: props.tutor?.id,
      materials: uploadedFiles.value, // Include uploaded materials
    };

    // Add location if face-to-face mode
    if (form.value.mode === "face-to-face") {
      sessionConfig.location = form.value.location.trim();
    }

    emit("session-configured", sessionConfig);
    emit("next-step");
  }
};
</script>

<style scoped>
.custom-select {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 0.75rem center;
  background-repeat: no-repeat;
  background-size: 1.5em 1.5em;
  padding-right: 2.5rem;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  color: #374151;
}

.custom-select:invalid {
  color: #6b7280;
}

.step-indicator {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}

.step-indicator.active {
  background-color: #3b82f6;
  color: white;
}

.step-indicator.completed {
  background-color: #10b981;
  color: white;
}

.step-indicator.inactive {
  background-color: #f3f4f6;
  color: #6b7280;
}

/* Custom textarea resize */
textarea {
  resize: vertical;
  min-height: 120px;
}

/* Custom focus states */
select:focus,
textarea:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Improve dropdown menu options */
.custom-select option {
  background-color: white;
  color: #374151;
  padding: 8px 12px;
  font-size: 14px;
  border-bottom: 1px solid #e5e7eb;
}

.custom-select option:hover {
  background-color: #8cc0ff;
  color: #1f2937;
}

.custom-select option:checked {
  background-color: #3b82f6;
  color: white;
  font-weight: 500;
}

.custom-select option:disabled {
  color: #9ca3af;
  background-color: #f9fafb;
}

.custom-select option[value=""] {
  color: #6b7280;
  font-style: normal;
}

/* Show placeholder text in select when nothing is selected */
.custom-select:has(option[value=""]:checked) {
  color: #6b7280;
}

button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
